import omit from 'lodash/omit'
import { computed } from 'vue'

import { useTeamStore } from '@/pinia/useTeamStore'
import type { Property, TeamProperty } from '@/store/types/PropertyTypes'
import { TeamPropertyGranularity } from '@/store/types/PropertyTypes'
import type {
  TeamPropertyRequestBody,
  TeamPropertyUpdateRequestBody,
} from '@/backend/darwin/teamProperties'
import { defineComposable } from '@/core/utils/defineComposable'

/** Used to link, update and unlink an annotation class property */
export const useAnnotationClassProperty = defineComposable(() => {
  const teamStore = useTeamStore()

  const teamSlug = computed(() => teamStore.currentTeam?.slug)

  const teamToClassProperty = (property: TeamProperty): Property => ({
    id: property.id,
    name: property.name,
    type: property.type,
    description: property.description || '',
    required: property.required,
    annotation_class_id: property.annotation_class_id,
    dataset_ids: property.dataset_ids,
    inserted_at: property.inserted_at,
    granularity: property.granularity || TeamPropertyGranularity.SECTION,
    position: property.position,
    parent_property_id: property.parent_property_id ?? null,
    trigger_condition: property.trigger_condition ?? null,
    min_duration_seconds: property.min_duration_seconds ?? null,
    max_duration_seconds: property.max_duration_seconds ?? null,
    values: property.property_values.map((propertyValue) => ({
      id: propertyValue.id,
      name: propertyValue.value,
      position: propertyValue.position,
      color: propertyValue.color || 'transparent',
    })),
  })

  const classToTeamProperties = (
    properties: Property[],
  ): Omit<TeamProperty, 'team_id' | 'slug'>[] =>
    properties.map((property) => ({
      id: property.id,
      name: property.name,
      type: property.type,
      description: property.description,
      required: property.required,
      annotation_class_id: property.annotation_class_id,
      dataset_ids: property.dataset_ids,
      inserted_at: property.inserted_at,
      granularity: property.granularity || TeamPropertyGranularity.SECTION,
      position: property.position,
      parent_property_id: property.parent_property_id ?? null,
      trigger_condition: property.trigger_condition ?? null,
      min_duration_seconds: property.min_duration_seconds ?? null,
      max_duration_seconds: property.max_duration_seconds ?? null,
      property_values: property.values.map((propertyValue) => ({
        id: propertyValue.id,
        color: propertyValue.color,
        position: propertyValue.position,
        value: propertyValue.name,
      })),
    }))

  const toTeamPropertyRequestBody = (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): TeamPropertyRequestBody => ({
    name: property.name,
    type: property.type,
    required: property.required,
    annotation_class_id: property.annotation_class_id,
    description: property.description,
    granularity: property.granularity || TeamPropertyGranularity.SECTION,
    min_duration_seconds: property.min_duration_seconds,
    max_duration_seconds: property.max_duration_seconds,
    property_values: property.property_values?.map((v) => omit(v, 'id', 'position')) || [],
    // `dataset_ids` is inherited from the root ancestor for nested properties
    // — the backend rejects explicit values with "Cannot be set on nested
    // properties; inherited from the root ancestor". Item-level nested
    // children carry `dataset_ids` on their in-memory state, so we have to
    // actively strip the field when a parent is linked.
    ...(!property.parent_property_id && { dataset_ids: property.dataset_ids }),
    // Nesting fields are only sent when a parent is linked (i.e. on create).
    // The update endpoint rejects them, which matches the immutability rule
    // for `parent_property_id` / `trigger_condition` in the TDD.
    ...(property.parent_property_id && {
      parent_property_id: property.parent_property_id,
      trigger_condition: property.trigger_condition,
    }),
  })

  // Update path: the backend treats `type`, `granularity`, `annotation_class_id`,
  // `parent_property_id` and `trigger_condition` as immutable after creation and
  // will reject them if present. We strip them here instead of relying on an ad
  // hoc `omit` at the call site, which had already drifted out of sync with the
  // `TeamPropertyUpdateRequestBody` contract.
  const toTeamPropertyUpdateBody = (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): TeamPropertyUpdateRequestBody => ({
    name: property.name,
    required: property.required,
    description: property.description,
    min_duration_seconds: property.min_duration_seconds,
    max_duration_seconds: property.max_duration_seconds,
    property_values: property.property_values?.map((v) => omit(v, 'id', 'position')) || [],
    // See `toTeamPropertyRequestBody`: `dataset_ids` is inherited from the
    // root ancestor on nested properties and the backend rejects explicit
    // values on update as well.
    ...(!property.parent_property_id && { dataset_ids: property.dataset_ids }),
  })

  return {
    teamSlug,
    teamToClassProperty,
    classToTeamProperties,
    toTeamPropertyRequestBody,
    toTeamPropertyUpdateBody,
  }
})
